import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, Stack,
  CircularProgress, Alert, Divider, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem,
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, AccountBalance, Edit, DriveFileMove } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expenseService, paymentService, projectService, farmOperationService, apiClient } from '@smartgrow/shared';
import { getErrorMessage } from '@smartgrow/shared/api/client';
import type { ExpenseStatus, CreateExpenseRequest } from '@smartgrow/shared';
import { useState } from 'react';

interface Category { id: string; name: string }

const STATUS_COLORS: Record<ExpenseStatus, 'default' | 'warning' | 'success' | 'error'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  PAID: 'default',
};

const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;

const Row = ({ label, value, onClick }: { label: string; value?: string | number | null; onClick?: () => void }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography
      variant="body2" fontWeight="medium"
      color={onClick ? 'primary' : undefined}
      sx={{ textAlign: 'right', maxWidth: '65%', ...(onClick && { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }) }}
      onClick={onClick}
    >
      {value ?? '—'}
    </Typography>
  </Box>
);

export const ExpenseDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const expenseId = id!;
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState('');
  const [disburseOpen, setDisburseOpen] = useState(false);
  const [disburseForm, setDisburseForm] = useState({
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'BANK_TRANSFER',
    referenceNumber: '',
    notes: '',
  });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<CreateExpenseRequest | null>(null);
  const [editError, setEditError] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveProjectId, setMoveProjectId] = useState('');
  const [moveOperationId, setMoveOperationId] = useState('');

  const userData = JSON.parse(localStorage.getItem('user_data') ?? '{}');
  const isAdmin = userData?.role === 'ADMIN' || userData?.role === 'OWNER';
  // Backend PUT /expenses/{id} only permits ADMIN/SUPERVISOR (create-side roles), not OWNER.
  const canEdit = userData?.role === 'ADMIN' || userData?.role === 'SUPERVISOR';

  const { data: expense, isLoading, isError } = useQuery({
    queryKey: ['expense', expenseId],
    queryFn: () => expenseService.getById(expenseId),
    enabled: !!expenseId,
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const res = await apiClient.get<Category[]>('/expenses/categories');
      return res.data;
    },
    enabled: editOpen,
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => projectService.getProjects({ size: 100 }),
    enabled: editOpen,
  });

  const projects = projectsData?.content ?? [];

  const { data: operationsData } = useQuery({
    queryKey: ['farm-operations-by-project', editForm?.projectId],
    queryFn: () => farmOperationService.getByProject(editForm!.projectId!),
    enabled: editOpen && !!editForm?.projectId,
  });

  const operations = operationsData?.content ?? [];

  const { data: tasks = [] } = useQuery({
    queryKey: ['operation-tasks-by-operation', editForm?.farmOperationId],
    queryFn: () => farmOperationService.getTasksByOperation(editForm!.farmOperationId!),
    enabled: editOpen && !!editForm?.farmOperationId,
  });

  const { data: projectsForMove } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
    enabled: moveOpen,
  });

  const { data: operationsForMove } = useQuery({
    queryKey: ['operations-for-filter', moveProjectId],
    queryFn: () => farmOperationService.getByProject(moveProjectId),
    enabled: moveOpen && !!moveProjectId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['expense', expenseId] });

  const moveMutation = useMutation({
    mutationFn: () => moveOperationId
      ? expenseService.moveToOperation(expenseId, moveOperationId)
      : expenseService.moveToProject(expenseId, moveProjectId),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['expenses'] });
      setActionError('');
      setMoveOpen(false);
      setMoveProjectId('');
      setMoveOperationId('');
    },
    onError: (err) => setActionError(getErrorMessage(err, 'Failed to move expense request.')),
  });

  const openMoveDialog = () => {
    setMoveProjectId('');
    setMoveOperationId('');
    setMoveOpen(true);
  };

  const approveMutation = useMutation({
    mutationFn: () => expenseService.approve(expenseId),
    onSuccess: () => { invalidate(); setActionError(''); },
    onError: () => setActionError('Approval failed.'),
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => expenseService.reject(expenseId, reason),
    onSuccess: () => { invalidate(); setRejectOpen(false); setRejectReason(''); setActionError(''); },
    onError: () => setActionError('Rejection failed.'),
  });

  const editMutation = useMutation({
    mutationFn: (data: CreateExpenseRequest) => expenseService.update(expenseId, data),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['expenses'] });
      setEditOpen(false);
      setActionError('');
    },
    onError: () => setEditError('Failed to update expense request.'),
  });

  const openEdit = () => {
    if (!expense) return;
    setEditForm({
      categoryId: expense.categoryId,
      projectId: expense.projectId,
      farmOperationId: expense.farmOperationId,
      operationTaskId: expense.operationTaskId,
      amount: expense.amount,
      description: expense.description,
      notes: expense.notes,
      receiptUrl: expense.receiptUrl,
      requestDate: expense.requestDate,
    });
    setEditError('');
    setEditOpen(true);
  };

  const submitEdit = () => {
    if (!editForm) return;
    if (!editForm.categoryId) { setEditError('Please select a category.'); return; }
    if (!editForm.amount || editForm.amount <= 0) { setEditError('Amount must be greater than 0.'); return; }
    if (!editForm.description) { setEditError('Description is required.'); return; }
    setEditError('');
    editMutation.mutate(editForm);
  };

  const disburseMutation = useMutation({
    mutationFn: () => paymentService.processExpensePayment({
      expenseRequestId: expenseId,
      amount: disburseForm.amount,
      paymentDate: disburseForm.paymentDate,
      paymentMethod: disburseForm.paymentMethod,
      referenceNumber: disburseForm.referenceNumber || undefined,
      notes: disburseForm.notes || undefined,
    }),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['expenses'] });
      setDisburseOpen(false);
      setActionError('');
    },
    onError: () => setActionError('Disbursement failed.'),
  });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  if (isError || !expense) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load expense request.</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="small">
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">{expense.description}</Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">{expense.requestNumber}</Typography>
        </Box>
        <Chip label={expense.status} color={STATUS_COLORS[expense.status]} size="small" />
      </Stack>

      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}

      {isAdmin && expense.status !== 'PAID' && (
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<DriveFileMove />}
            onClick={openMoveDialog}
          >
            Move
          </Button>
        </Stack>
      )}

      {expense.status === 'PENDING' && (
        <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap">
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={openEdit}
            >
              Edit
            </Button>
          )}
          {isAdmin && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={() => { setRejectOpen(true); setRejectReason(''); }}
              >
                Reject
              </Button>
            </>
          )}
        </Stack>
      )}

      {isAdmin && expense.status === 'APPROVED' && (
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<AccountBalance />}
            onClick={() => {
              setDisburseForm((f) => ({ ...f, amount: expense.amount }));
              setDisburseOpen(true);
            }}
          >
            Disburse
          </Button>
        </Stack>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Expense Details</Typography>
              <Divider sx={{ mb: 1 }} />
              <Row label="Amount" value={fmt(expense.amount)} />
              {expense.amountPaid != null && <Row label="Amount Paid" value={fmt(expense.amountPaid)} />}
              <Row label="Category" value={expense.categoryName} />
              <Row label="Request Date" value={expense.requestDate} />
              <Row label="Requested By" value={expense.requestedByName} />
              {expense.notes && <Row label="Notes" value={expense.notes} />}
              {expense.receiptUrl && <Row label="Receipt" value={expense.receiptUrl} />}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Linked To</Typography>
              <Divider sx={{ mb: 1 }} />
              <Row label="Project" value={expense.projectName}
                onClick={expense.projectId ? () => navigate(`/dashboard/projects/${expense.projectId}`) : undefined} />
              <Row label="Farm Operation" value={expense.farmOperationName}
                onClick={expense.farmOperationId ? () => navigate(`/dashboard/farm/${expense.farmOperationId}`) : undefined} />
              <Row label="Task" value={expense.operationTaskTitle} />
            </CardContent>
          </Card>
        </Grid>

        {(expense.approvedByName || expense.rejectionReason) && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Review</Typography>
                <Divider sx={{ mb: 1 }} />
                {expense.approvedByName && <Row label="Reviewed By" value={expense.approvedByName} />}
                {expense.approvalDate && <Row label="Review Date" value={expense.approvalDate} />}
                {expense.rejectionReason && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">Rejection Reason</Typography>
                    <Alert severity="error" sx={{ mt: 0.5 }}>{expense.rejectionReason}</Alert>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Expense Request</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editForm && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
              {editError && <Alert severity="error" onClose={() => setEditError('')}>{editError}</Alert>}
              <TextField
                select fullWidth required label="Category"
                value={editForm.categoryId}
                onChange={(e) => setEditForm((f) => f && { ...f, categoryId: e.target.value })}
              >
                {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
              <TextField
                fullWidth required type="number" label="Amount (₦)"
                value={editForm.amount || ''}
                onChange={(e) => setEditForm((f) => f && { ...f, amount: Number.parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 1 }}
              />
              <TextField
                fullWidth required multiline rows={3} label="Description"
                value={editForm.description}
                onChange={(e) => setEditForm((f) => f && { ...f, description: e.target.value })}
              />
              <TextField
                select fullWidth label="Project (optional)"
                value={editForm.projectId ?? ''}
                onChange={(e) => setEditForm((f) => f && {
                  ...f,
                  projectId: e.target.value || undefined,
                  farmOperationId: undefined,
                  operationTaskId: undefined,
                })}
              >
                <MenuItem value="">— None —</MenuItem>
                {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>)}
              </TextField>
              <TextField
                select fullWidth label="Farm Operation (optional)"
                value={editForm.farmOperationId ?? ''}
                disabled={!editForm.projectId}
                helperText={!editForm.projectId ? 'Select a project first' : undefined}
                onChange={(e) => setEditForm((f) => f && {
                  ...f,
                  farmOperationId: e.target.value || undefined,
                  operationTaskId: undefined,
                })}
              >
                <MenuItem value="">— None —</MenuItem>
                {operations.map((op) => <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>)}
              </TextField>
              <TextField
                select fullWidth label="Task (optional)"
                value={editForm.operationTaskId ?? ''}
                disabled={!editForm.farmOperationId}
                helperText={!editForm.farmOperationId ? 'Select a farm operation first' : undefined}
                onChange={(e) => setEditForm((f) => f && { ...f, operationTaskId: e.target.value || undefined })}
              >
                <MenuItem value="">— None —</MenuItem>
                {tasks.map((t) => <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>)}
              </TextField>
              <TextField
                label="Request Date"
                type="date"
                value={editForm.requestDate ?? ''}
                onChange={(e) => setEditForm((f) => f && { ...f, requestDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
              />
              <TextField
                fullWidth multiline rows={2} label="Notes (optional)"
                value={editForm.notes ?? ''}
                onChange={(e) => setEditForm((f) => f && { ...f, notes: e.target.value })}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={editMutation.isPending}
            onClick={submitEdit}
          >
            {editMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Expense</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {expense.description} — {fmt(expense.amount)}
          </Typography>
          <TextField
            fullWidth required multiline rows={3} label="Rejection Reason"
            value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            variant="contained" color="error"
            disabled={!rejectReason || rejectMutation.isPending}
            onClick={() => rejectMutation.mutate(rejectReason)}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={disburseOpen} onClose={() => setDisburseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Disburse Expense — {expense.requestNumber}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {expense.description} — {fmt(expense.amount)}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Amount to Disburse (₦)"
              type="number"
              value={disburseForm.amount || ''}
              onChange={(e) => setDisburseForm((f) => ({ ...f, amount: parseFloat(e.target.value) || 0 }))}
              fullWidth
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <TextField
              label="Payment Date"
              type="date"
              value={disburseForm.paymentDate}
              onChange={(e) => setDisburseForm((f) => ({ ...f, paymentDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Payment Method"
              value={disburseForm.paymentMethod}
              onChange={(e) => setDisburseForm((f) => ({ ...f, paymentMethod: e.target.value }))}
              fullWidth
            >
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="MOBILE_MONEY">Mobile Money</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </TextField>
            <TextField
              label="Reference Number (optional)"
              value={disburseForm.referenceNumber}
              onChange={(e) => setDisburseForm((f) => ({ ...f, referenceNumber: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Notes (optional)"
              multiline rows={2}
              value={disburseForm.notes}
              onChange={(e) => setDisburseForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisburseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!disburseForm.amount || disburseForm.amount <= 0 || !disburseForm.paymentDate || disburseMutation.isPending}
            startIcon={disburseMutation.isPending ? <CircularProgress size={16} /> : <AccountBalance />}
            onClick={() => disburseMutation.mutate()}
          >
            Confirm Disbursement
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveOpen} onClose={() => setMoveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Move Expense Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move this expense request to a different project, or to a specific farm operation within it.
          </Typography>
          <TextField select fullWidth label="Target Project" value={moveProjectId} sx={{ mb: 2 }}
            onChange={(e) => { setMoveProjectId(e.target.value); setMoveOperationId(''); }}>
            {(projectsForMove?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select fullWidth label="Farm Operation (optional)" value={moveOperationId} disabled={!moveProjectId}
            helperText={!moveProjectId ? 'Select a project first' : 'Leave blank to move to the project directly'}
            onChange={(e) => setMoveOperationId(e.target.value)}>
            <MenuItem value="">— None —</MenuItem>
            {(operationsForMove?.content ?? []).map((op) => (
              <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!moveProjectId || moveMutation.isPending}
            onClick={() => moveMutation.mutate()}
          >
            {moveMutation.isPending ? <CircularProgress size={20} /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
